import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Link, Redirect } from "react-router-dom";
import {
  FaCalendarAlt,
  FaCheck,
  FaClipboardList,
  FaEdit,
  FaExchangeAlt,
  FaFileAlt,
  FaLayerGroup,
  FaListOl,
  FaPlus,
  FaSyncAlt,
  FaTable,
  FaThLarge,
  FaTimes,
  FaTrash,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import BackButton from "../ChildComponent/BackButton";
import MdlSelectSession from "../dialog/MdlSelectSession";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  '"Segoe UI", system-ui, -apple-system, Roboto, "Helvetica Neue", Arial, sans-serif';

function getConcernedExam(item) {
  const exams = item?.exams_list || [];
  return exams.map((exam) => exam.exam_name).filter(Boolean).join(", ");
}

function getLastConcernedExams(item) {
  if (!item || !Array.isArray(item.exams_list) || item.exams_list.length === 0) {
    return "0";
  }
  const last = item.exams_list[item.exams_list.length - 1];
  return last?.concerned_exams || "0";
}

function sortReportCards(data) {
  const extractNum = (rc) => {
    if (!rc?.report_card_id) return 0;
    const m = String(rc.report_card_id).match(/RC(\d+)/i);
    return m ? parseInt(m[1], 10) : 0;
  };
  return [...data].sort((a, b) => extractNum(b) - extractNum(a));
}

//Created By Cursor
const ReportCardItem = memo(function ReportCardItem({
  item,
  index,
  busy,
  onEdit,
  onDelete,
}) {
  const concerned = getConcernedExam(item);
  const lastExam = getLastConcernedExams(item) || "0";
  const reportCardId = item.report_card_id || "";
  const sessionId = item.session_id || "";

  const markingTo = `/AdminMain/ExamMarkingSystemAnnual/${reportCardId}/${sessionId}`;
  const links = [
    {
      to: `/AdminMain/ExamStudentReportCard/${reportCardId}/${encodeURIComponent(
        String(lastExam)
      )}/${sessionId}/null`,
      label: "Mark Sheet",
      icon: FaFileAlt,
      tone: "mark",
    },
    {
      to: `/AdminMain/ExamReportCardRankList/${reportCardId}/${sessionId}`,
      label: "Rank List",
      icon: FaListOl,
      tone: "rank",
    },
    {
      to: `/AdminMain/ExamReportCardResultSheet/${reportCardId}/${sessionId}`,
      label: "Result Sheet",
      icon: FaClipboardList,
      tone: "result",
    },
    {
      to: `/AdminMain/ExamReportCardCrossList/${reportCardId}/${sessionId}`,
      label: "Cross List",
      icon: FaTable,
      tone: "cross",
    },
    {
      to: `/AdminMain/ExamReportCardGauzzat/${reportCardId}/${sessionId}`,
      label: "Gauzzat",
      icon: FaThLarge,
      tone: "gauzzat",
    },
  ];

  return (
    <article className="ercl-card" style={{ "--ercl-i": index }}>
      <div className="ercl-card__head">
        <div className="min-w-0">
          <h3 className="ercl-card__title" title={item.report_card_name || ""}>
            {item.report_card_name || "—"}
          </h3>
        </div>
        <div className="ercl-card__actions">
          <Link
            to={markingTo}
            className="ercl-marking-btn"
            title="Performance Descriptor"
          >
            <FaLayerGroup aria-hidden />
            <span>Performance Descriptor</span>
          </Link>
          <button
            type="button"
            className="ercl-icon-btn ercl-icon-btn--edit"
            title="Edit report card"
            disabled={busy}
            onClick={() => onEdit(item)}
          >
            <FaEdit />
          </button>
          <button
            type="button"
            className="ercl-icon-btn ercl-icon-btn--delete"
            title="Delete report card"
            disabled={busy}
            onClick={() => onDelete(item)}
          >
            <FaTrash />
          </button>
        </div>
      </div>

      <div className="ercl-card__meta">
        <div className="ercl-meta-block">
          <span className="ercl-meta-block__label">Concerned exams</span>
          <p className="ercl-meta-block__value" title={concerned || ""}>
            {concerned || "—"}
          </p>
        </div>
        <div className="ercl-meta-block">
          <span className="ercl-meta-block__label">Remark</span>
          <p className="ercl-meta-block__value">
            {item.report_card_remark || "No remarks"}
          </p>
        </div>
      </div>

      <div className="ercl-card__links">
        {links.map((link) => {
          const Icon = link.icon;
          return (
            <Link
              key={link.tone}
              to={link.to}
              className={`ercl-link ercl-link--${link.tone}`}
            >
              <Icon aria-hidden />
              <span>{link.label}</span>
            </Link>
          );
        })}
      </div>
    </article>
  );
});

//Created By Cursor
function MdlEditReportCard({
  open,
  caption,
  remark,
  saving,
  onCaptionChange,
  onRemarkChange,
  onClose,
  onSave,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !saving && onClose) onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, saving, onClose]);

  if (!open) return null;

  const modal = (
    <div
      className="ercl-mdl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ercl-mdl-title"
    >
      <button
        type="button"
        className="ercl-mdl__backdrop"
        aria-label="Close"
        onClick={onClose}
        disabled={saving}
      />
      <div className="ercl-mdl__panel">
        <header className="ercl-mdl__hero">
          <div className="ercl-mdl__hero-icon" aria-hidden>
            <FaEdit />
          </div>
          <div className="min-w-0">
            <h2 id="ercl-mdl-title" className="ercl-mdl__title">
              Edit report card
            </h2>
            <p className="ercl-mdl__sub">Update caption and remark</p>
          </div>
          <button
            type="button"
            className="ercl-mdl__close"
            onClick={onClose}
            disabled={saving}
            aria-label="Close dialog"
          >
            <FaTimes />
          </button>
        </header>
        <div className="ercl-mdl__body">
          <label className="ercl-field">
            <span>Report card caption</span>
            <input
              type="text"
              value={caption}
              onChange={(e) => onCaptionChange(e.target.value)}
              placeholder="Enter report card caption"
              disabled={saving}
            />
          </label>
          <label className="ercl-field">
            <span>Remark</span>
            <input
              type="text"
              value={remark}
              onChange={(e) => onRemarkChange(e.target.value)}
              placeholder="Enter remark"
              disabled={saving}
            />
          </label>
        </div>
        <footer className="ercl-mdl__footer">
          <button
            type="button"
            className="ercl-btn ercl-btn--ghost"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="ercl-btn ercl-btn--primary"
            onClick={onSave}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </footer>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

//Created By Cursor
export default function ExamReportCardList() {
  const loggedIn = localStorage.getItem("token") != null;

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [isOpenSessionDialog, setIsOpenSessionDialog] = useState(false);
  const [isLoadingExams, setIsLoadingExams] = useState(false);
  const [mExamReportCardList, setExamReportCardList] = useState([]);
  const [mExamList, setExamList] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [sessionName, setSessionName] = useState("");
  const [reportCardCaption, setReportCardCaption] = useState("Progress Report");
  const [reportCardRemark, setReportCardRemark] = useState("");
  const [selectedExamIds, setSelectedExamIds] = useState([]);
  const selectedExamIdsRef = useRef([]);
  const [creating, setCreating] = useState(false);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editCaption, setEditCaption] = useState("");
  const [editRemark, setEditRemark] = useState("");
  const [editReportCardId, setEditReportCardId] = useState("");

  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const getExamReportCardList = useCallback(
    async (sessionIdParam = null, { soft = false } = {}) => {
      const schoolCode = SessionManager.getSchoolCode();
      const sid = sessionIdParam || sessionId;
      if (!schoolCode || !sid) {
        myToast.notify_warning("Missing session or school code.");
        setExamReportCardList([]);
        return;
      }
      if (soft) setRefreshing(true);
      else setLoading(true);
      try {
        const { data: response } = await axios.get(
          `${API_BASE()}getSchoolRepordCardList.php`,
          { params: { school_code: schoolCode, session_id: sid } }
        );
        if (!mountedRef.current) return;
        if (response?.api_status === "OK" && Array.isArray(response.data)) {
          setExamReportCardList(sortReportCards(response.data));
        } else {
          setExamReportCardList([]);
        }
      } catch (err) {
        if (!mountedRef.current) return;
        setExamReportCardList([]);
        myToast.notify_warning("Failed to load report cards.");
      } finally {
        if (mountedRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [sessionId]
  );

  const getExamList = useCallback(
    async (sessionIdParam = null) => {
      const schoolCode = SessionManager.getSchoolCode();
      const sid = sessionIdParam || sessionId;
      setIsLoadingExams(true);
      try {
        if (!schoolCode || !sid) {
          myToast.notify_warning("Missing session or school code.");
          if (!sid) setIsOpenSessionDialog(true);
          return;
        }
        const { data: response } = await axios.get(
          `${API_BASE()}getExamList.php`,
          { params: { school_code: schoolCode, session_id: sid } }
        );
        if (!mountedRef.current) return;
        if (response?.api_status === "OK" && Array.isArray(response.data)) {
          setExamList([...response.data].reverse());
        } else {
          setExamList([]);
          myToast.notify_warning(
            response?.api_remark || "Failed to fetch exam list"
          );
        }
      } catch (err) {
        if (!mountedRef.current) return;
        setExamList([]);
        myToast.notify_warning("Error fetching exam list");
      } finally {
        if (mountedRef.current) setIsLoadingExams(false);
      }
    },
    [sessionId]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    const mSession = SessionManager.getCurrentSession?.();
    if (mSession && mSession[0]) {
      const sid = mSession[0].session_id;
      const sname = mSession[0].session_name;
      setSessionId(sid);
      setSessionName(sname);
      setReportCardCaption(`Progress Report ${sname}`);
      getExamReportCardList(sid);
      getExamList(sid);
    } else {
      setIsOpenSessionDialog(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleExamSelection = useCallback((examId) => {
    const id = String(examId).trim();
    const current = Array.isArray(selectedExamIdsRef.current)
      ? selectedExamIdsRef.current.map((x) => String(x).trim())
      : [];
    const next = current.includes(id)
      ? current.filter((x) => x !== id)
      : [...current, id];
    selectedExamIdsRef.current = next;
    setSelectedExamIds(next);
  }, []);

  const handleSelectedSession = useCallback(
    (sid, sname) => {
      setSessionId(sid);
      setSessionName(sname);
      setIsOpenSessionDialog(false);
      setExamReportCardList([]);
      setSelectedExamIds([]);
      selectedExamIdsRef.current = [];
      setReportCardCaption(`Progress Report ${sname}`);
      getExamReportCardList(sid);
      getExamList(sid);
    },
    [getExamList, getExamReportCardList]
  );

  const deleteReportCard = useCallback(
    async (item) => {
      if (
        !window.confirm(
          `Are you sure you want to delete "${item.report_card_name}"?`
        )
      ) {
        return;
      }
      const schoolCode = SessionManager.getSchoolCode();
      const staffId = SessionManager.getStaffDetails()?.staff_id;
      if (!schoolCode || !sessionId || !item.report_card_id || !staffId) {
        myToast.notify_warning("Missing required parameters for deletion");
        return;
      }
      setLoading(true);
      try {
        const { data: response } = await axios.get(
          `${API_BASE()}DeleteReportCardMapping.php`,
          {
            params: {
              school_code: schoolCode,
              session_id: sessionId,
              report_card_id: item.report_card_id,
              staff_id: staffId,
            },
          }
        );
        if (!mountedRef.current) return;
        if (response?.api_status === "OK") {
          myToast.notify_success("Report card deleted successfully.");
          getExamReportCardList(sessionId, { soft: true });
        } else {
          myToast.notify_warning(
            response?.api_remark || "Failed to delete report card"
          );
        }
      } catch (err) {
        myToast.notify_warning("Error deleting report card");
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    [sessionId, getExamReportCardList]
  );

  const openEditDialog = useCallback((item) => {
    if (!item) return;
    setEditReportCardId(item.report_card_id || "");
    setEditCaption(item.report_card_name || "");
    setEditRemark(item.report_card_remark || "");
    setIsEditOpen(true);
  }, []);

  const closeEditDialog = useCallback(() => {
    if (editSaving) return;
    setIsEditOpen(false);
  }, [editSaving]);

  const saveEditDialog = useCallback(async () => {
    if (!editReportCardId) {
      myToast.notify_warning("Missing report card id");
      return;
    }
    const schoolCode = SessionManager.getSchoolCode();
    setEditSaving(true);
    try {
      const { data: response } = await axios.get(
        `${API_BASE()}updateExamReportCard.php`,
        {
          params: {
            school_code: schoolCode,
            session_id: sessionId,
            report_card_id: editReportCardId,
            report_card_name: (editCaption || "").trim(),
            report_card_remark: editRemark ?? "",
          },
        }
      );
      if (response?.api_status === "OK") {
        myToast.notify_success("Report card updated successfully.");
        setIsEditOpen(false);
        getExamReportCardList(sessionId, { soft: true });
      } else {
        myToast.notify_warning(
          response?.api_remark || "Failed to update report card"
        );
      }
    } catch (err) {
      myToast.notify_warning("Error updating report card");
    } finally {
      setEditSaving(false);
    }
  }, [
    sessionId,
    editReportCardId,
    editCaption,
    editRemark,
    getExamReportCardList,
  ]);

  const submitReportCard = useCallback(async () => {
    const selectedIds = Array.isArray(selectedExamIdsRef.current)
      ? selectedExamIdsRef.current.map((x) => String(x).trim()).filter(Boolean)
      : [];
    if (selectedIds.length === 0) {
      myToast.notify_warning("Please select at least one exam.");
      return;
    }
    const schoolCode = SessionManager.getSchoolCode();
    const currentSessionId =
      sessionId ||
      SessionManager.getCurrentSession?.()?.[0]?.session_id ||
      "";
    if (!schoolCode || !currentSessionId) {
      myToast.notify_warning("Missing required parameters");
      return;
    }
    setCreating(true);
    try {
      const { data: response } = await axios.get(
        `${API_BASE()}createExamReportCard2.php`,
        {
          params: {
            school_code: schoolCode,
            session_id: currentSessionId,
            report_card_name: (reportCardCaption || "").trim(),
            report_card_remark: reportCardRemark ?? "",
            str_exam_list: JSON.stringify(selectedIds),
            report_card_pattern_id: "0",
          },
        }
      );
      if (!mountedRef.current) return;
      if (response?.api_status === "OK") {
        myToast.notify_success("Report card created successfully.");
        selectedExamIdsRef.current = [];
        setSelectedExamIds([]);
        setReportCardRemark("");
        getExamReportCardList(currentSessionId, { soft: true });
      } else {
        myToast.notify_warning(
          response?.api_remark || "Failed to create report card"
        );
      }
    } catch (err) {
      myToast.notify_warning("Error creating report card");
    } finally {
      if (mountedRef.current) setCreating(false);
    }
  }, [sessionId, reportCardCaption, reportCardRemark, getExamReportCardList]);

  const selectableExams = useMemo(
    () =>
      mExamList.filter(
        (e) =>
          String(e.exam_type || "EXAM")
            .trim()
            .toUpperCase() === "EXAM"
      ),
    [mExamList]
  );

  const selectedCount = selectedExamIds.length;
  const busy = loading || refreshing || creating || editSaving;

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div
      className="content payroll-page sm-page ercl-page"
      style={{ background: COLORS.white, fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Exam Report Card List</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell ercl-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaClipboardList />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Exam Report Cards</h1>
              <p className="sm-hero__sub">
                {sessionName
                  ? `${sessionName} · ${mExamReportCardList.length} report card${
                      mExamReportCardList.length === 1 ? "" : "s"
                    }`
                  : "Select a session to manage report cards"}
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              className="ercl-session-btn"
              onClick={() => setIsOpenSessionDialog(true)}
              title="Change academic session"
            >
              <FaCalendarAlt aria-hidden />
              <span className="min-w-0">
                <span className="ercl-session-btn__label">Session</span>
                <strong>{sessionName || "Select session"}</strong>
              </span>
              <FaExchangeAlt aria-hidden />
            </button>
            <button
              type="button"
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              onClick={() => getExamReportCardList(sessionId, { soft: true })}
              disabled={busy || !sessionId}
              aria-label="Refresh"
              title="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="ercl-body">
          {!sessionId ? (
            <div className="ercl-empty">
              <div className="ercl-empty__icon" aria-hidden>
                <FaCalendarAlt />
              </div>
              <p className="ercl-empty__title">Select a session</p>
              <p className="ercl-empty__hint">
                Choose an academic session to load and create report cards
              </p>
              <button
                type="button"
                className="ercl-btn ercl-btn--primary"
                onClick={() => setIsOpenSessionDialog(true)}
              >
                Choose session
              </button>
            </div>
          ) : loading ? (
            <div className="ercl-loading">
              <MyLoader />
            </div>
          ) : (
            <>
              <section className="ercl-panel" aria-label="Existing report cards">
                <div className="ercl-panel__head">
                  <h2>
                    <FaClipboardList aria-hidden /> Existing report cards
                  </h2>
                  <span>{mExamReportCardList.length} shown</span>
                </div>

                {mExamReportCardList.length === 0 ? (
                  <div className="ercl-empty ercl-empty--soft">
                    <p className="ercl-empty__title">No report cards yet</p>
                    <p className="ercl-empty__hint">
                      Create your first report card below.
                    </p>
                  </div>
                ) : (
                  <div className="ercl-list">
                    {mExamReportCardList.map((item, index) => (
                      <ReportCardItem
                        key={item.report_card_id}
                        item={item}
                        index={index}
                        busy={busy}
                        onEdit={openEditDialog}
                        onDelete={deleteReportCard}
                      />
                    ))}
                  </div>
                )}
              </section>

              <section className="ercl-create" aria-label="Create report card">
                <div className="ercl-create__head">
                  <div className="ercl-create__icon" aria-hidden>
                    <FaPlus />
                  </div>
                  <div>
                    <h2>Create new report card</h2>
                    <p>Select exams in the order they should appear</p>
                  </div>
                </div>

                <div className="ercl-create__form">
                  <label className="ercl-field">
                    <span>Report card caption</span>
                    <input
                      type="text"
                      value={reportCardCaption}
                      onChange={(e) => setReportCardCaption(e.target.value)}
                      placeholder="Enter report card caption"
                      disabled={creating}
                    />
                  </label>
                  <label className="ercl-field">
                    <span>Remark</span>
                    <input
                      type="text"
                      value={reportCardRemark}
                      onChange={(e) => setReportCardRemark(e.target.value)}
                      placeholder="Enter remark"
                      disabled={creating}
                    />
                  </label>
                </div>

                <div className="ercl-exams">
                  <div className="ercl-exams__head">
                    <div className="ercl-exams__title">
                      <span className="ercl-exams__title-icon" aria-hidden>
                        <FaListOl />
                      </span>
                      <div>
                        <h3>Select exams</h3>
                        <p>Tap exams in the order they should appear on the report</p>
                      </div>
                    </div>
                    <span className="ercl-exams__count">
                      {selectedCount} selected
                    </span>
                  </div>

                  {selectedCount > 0 ? (
                    <div className="ercl-order" aria-label="Selected exam order">
                      <span className="ercl-order__label">Order</span>
                      <div className="ercl-order__list">
                        {selectedExamIds.map((sid, idx) => {
                          const exam = selectableExams.find(
                            (e) => String(e.exam_id).trim() === String(sid).trim()
                          );
                          return (
                            <span key={sid} className="ercl-order__item">
                              <em>{idx + 1}</em>
                              {exam?.exam_name || sid}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}

                  {isLoadingExams ? (
                    <div className="ercl-exams__loading">Loading exams…</div>
                  ) : selectableExams.length === 0 ? (
                    <div className="ercl-exams__empty">
                      No exams found for this session.
                    </div>
                  ) : (
                    <div className="ercl-exams__grid">
                      {selectableExams.map((exam, examIndex) => {
                        const id = String(exam.exam_id).trim();
                        const checked = selectedExamIds
                          .map((x) => String(x).trim())
                          .includes(id);
                        const order = checked
                          ? selectedExamIds
                              .map((x) => String(x).trim())
                              .indexOf(id) + 1
                          : 0;
                        return (
                          <label
                            key={id}
                            className={`ercl-exam${checked ? " is-checked" : ""}`}
                            style={{ "--ercl-exam-i": examIndex }}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleExamSelection(id)}
                              disabled={creating}
                            />
                            <span className="ercl-exam__check" aria-hidden>
                              {checked ? <FaCheck /> : null}
                            </span>
                            <span className="ercl-exam__body">
                              <span className="ercl-exam__name">
                                {exam.exam_name}
                              </span>
                              <span className="ercl-exam__id">EXAM</span>
                            </span>
                            {checked ? (
                              <span className="ercl-exam__order">{order}</span>
                            ) : (
                              <span className="ercl-exam__plus" aria-hidden>
                                <FaPlus />
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  )}
                  {!isLoadingExams && selectedCount === 0 ? (
                    <p className="ercl-exams__hint">
                      Please select at least one exam to create a report card.
                    </p>
                  ) : null}
                </div>

                <div className="ercl-create__footer">
                  <button
                    type="button"
                    className="ercl-btn ercl-btn--primary ercl-btn--lg"
                    onClick={submitReportCard}
                    disabled={
                      creating || isLoadingExams || selectedCount === 0
                    }
                  >
                    <FaPlus aria-hidden />
                    {creating ? "Creating…" : "Create report card"}
                  </button>
                </div>
              </section>
            </>
          )}
        </div>
      </div>

      <MdlEditReportCard
        open={isEditOpen}
        caption={editCaption}
        remark={editRemark}
        saving={editSaving}
        onCaptionChange={setEditCaption}
        onRemarkChange={setEditRemark}
        onClose={closeEditDialog}
        onSave={saveEditDialog}
      />

      {isOpenSessionDialog ? (
        <MdlSelectSession
          action={() => setIsOpenSessionDialog(false)}
          action2={handleSelectedSession}
        />
      ) : null}
    </div>
  );
}
